import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Chip,
  Drawer,
  Divider,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Send,
  Smartphone,
  Mail,
  MessageSquare,
  MessageCircle,
  Settings,
  Clock,
  Eye,
  EyeOff,
  Layers,
  ChevronDown,
  ChevronRight,
  X,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import {
  sendNotificationBroadcast,
  getNotificationHistory,
  getSmsProvidersConfig,
  updateSmsProvidersConfig,
  testSmsConnection,
  checkAllBalances,
  getBroadcastDetails,
} from '../api/notifications.api';

// ── Data ─────────────────────────────────────────────────────────────────────

const INITIAL_SMS_PROVIDERS = [
  {
    name: 'HollaTags',
    slug: 'hollatags',
    tagline: 'Direct telco routes & WhatsApp',
    status: 'disabled',
    isDefault: true,
    balanceUnits: 0,
    balanceNaira: 0,
    senderId: '',
    username: '',
    password: '',
    whatsappSender: '',
    baseUrl: 'https://sms.hollatags.com/api/send',
    dndPolicy: 'corporate',
    deliveryRate: 0,
  },
  {
    name: 'KudiSMS',
    slug: 'kudisms',
    tagline: 'High-volume bulk SMS, Email & WhatsApp',
    status: 'disabled',
    isDefault: false,
    balanceUnits: 0,
    balanceNaira: 0,
    senderId: '',
    apiKey: '',
    whatsappSender: '',
    emailSender: '',
    emailSenderName: '',
    baseUrl: 'https://my.kudisms.net/api/sms',
    dndPolicy: 'corporate',
    deliveryRate: 0,
  },
];

const INITIAL_RECENT_DISPATCHES = [];

const AUDIENCES = [
  { value: 'all', label: 'All Registered Users' },
  { value: 'verified', label: 'Email Verified Users' },
  { value: 'agents', label: 'Agents & Resellers' },
  { value: 'specific', label: 'Specific User or Number' },
];

const CHANNELS = [
  { key: 'push', label: 'Push', icon: Smartphone },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'sms', label: 'SMS', icon: MessageSquare },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { key: 'multi', label: 'All', icon: Layers },
];

const CHANNEL_LABEL = { push: 'Push', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp', multi: 'Multi' };

const CHANNEL_CARDS = [
  {
    key: 'sms',
    title: 'SMS',
    subtitle: 'Text messaging',
    icon: MessageSquare,
    brandColor: '#059669',
    brandBg: '#ECFDF5',
  },
  {
    key: 'email',
    title: 'Email',
    subtitle: 'Newsletters & alerts',
    icon: Mail,
    brandColor: '#0284C7',
    brandBg: '#E0F2FE',
  },
  {
    key: 'push',
    title: 'Push',
    subtitle: 'In-app & lockscreen',
    icon: Smartphone,
    brandColor: '#4F46E5',
    brandBg: '#EEF2FF',
  },
  {
    key: 'whatsapp',
    title: 'WhatsApp',
    subtitle: 'Direct chat broadcast',
    icon: MessageCircle,
    brandColor: '#128C7E',
    brandBg: '#DCFCE7',
  },
  {
    key: 'multi',
    title: 'All Channels',
    subtitle: 'Omni broadcast',
    icon: Layers,
    brandColor: '#7C3AED',
    brandBg: '#F3E8FF',
  },
];

// ── Small helpers ────────────────────────────────────────────────────────────

function ChannelIcon({ channel, size = 16 }) {
  const item = CHANNELS.find((c) => c.key === channel) || CHANNELS[0];
  const Icon = item.icon;
  return <Icon size={size} />;
}

ChannelIcon.propTypes = {
  channel: PropTypes.string,
  size: PropTypes.number,
};

function FieldLabel({ children }) {
  return (
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
      {children}
    </Typography>
  );
}

FieldLabel.propTypes = {
  children: PropTypes.node,
};

// ----------------------------------------------------------------------

export default function NotificationsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const line = alpha(theme.palette.divider, 0.9);

  const { activeApp: reduxActiveApp } = useSelector((state) => state.auth || {});
  let activeApp = reduxActiveApp;
  if (!activeApp) {
    try {
      const stored = localStorage.getItem('activeApp');
      if (stored) activeApp = JSON.parse(stored);
    } catch (e) {
      // ignore
    }
  }

  const defaultSenderId = useMemo(
    () =>
      (activeApp?.companyInit || activeApp?.appName || activeApp?.name || '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, 11)
        .toUpperCase(),
    [activeApp]
  );

  const [mainTab, setMainTab] = useState('overview');
  const [channel, setChannel] = useState('sms');
  const [isComposeDrawerOpen, setIsComposeDrawerOpen] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);

  const [audience, setAudience] = useState('all');
  const [specificTarget, setSpecificTarget] = useState('');

  // Push
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushActionUrl, setPushActionUrl] = useState('');
  const [pushPriority, setPushPriority] = useState('high');

  // Email
  const [emailSubject, setEmailSubject] = useState('');
  const [emailPreheader, setEmailPreheader] = useState('');
  const [emailSenderName, setEmailSenderName] = useState(defaultSenderId || '');
  const [emailBody, setEmailBody] = useState('');
  const [emailCtaText, setEmailCtaText] = useState('');
  const [emailCtaUrl, setEmailCtaUrl] = useState('');
  const [showEmailExtras, setShowEmailExtras] = useState(false);

  // SMS
  const [smsSenderId, setSmsSenderId] = useState(defaultSenderId || '');
  const [smsRoute, setSmsRoute] = useState('corporate');
  const [smsBody, setSmsBody] = useState('');

  // WhatsApp
  const [whatsappHeader, setWhatsappHeader] = useState('');
  const [whatsappBody, setWhatsappBody] = useState('');
  const [whatsappButtonText, setWhatsappButtonText] = useState('');
  const [whatsappButtonUrl, setWhatsappButtonUrl] = useState('');
  const [showWhatsappExtras, setShowWhatsappExtras] = useState(false);

  // Multi
  const [multiPush, setMultiPush] = useState(true);
  const [multiEmail, setMultiEmail] = useState(true);
  const [multiSms, setMultiSms] = useState(false);
  const [multiWhatsapp, setMultiWhatsapp] = useState(false);
  const [multiTitle, setMultiTitle] = useState('');
  const [multiBody, setMultiBody] = useState('');

  const [sending, setSending] = useState(false);

  // SMS providers
  const [isSmsConfigOpen, setIsSmsConfigOpen] = useState(false);
  const [smsProviders, setSmsProviders] = useState(INITIAL_SMS_PROVIDERS);
  const [selectedProviderSlug, setSelectedProviderSlug] = useState('hollatags');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [testingSms, setTestingSms] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const [dispatches, setDispatches] = useState(INITIAL_RECENT_DISPATCHES);

  useEffect(() => {
    if (defaultSenderId) {
      setSmsSenderId((prev) => prev || defaultSenderId);
      setEmailSenderName((prev) => prev || defaultSenderId);
    }
  }, [defaultSenderId]);

  const activeSmsProvider = useMemo(
    () => smsProviders.find((p) => p.slug === selectedProviderSlug) || smsProviders[0],
    [smsProviders, selectedProviderSlug]
  );

  const defaultSmsProvider = useMemo(() => smsProviders.find((p) => p.isDefault) || smsProviders[0], [smsProviders]);

  // SMS counters
  const smsCharCount = smsBody.length;
  const smsPageCount = smsCharCount <= 160 ? 1 : Math.ceil(smsCharCount / 153);

  // ── Remote API Fetchers ────────────────────────────────────────────────────

  const fetchBalances = useCallback(async (refresh = false) => {
    try {
      const res = await checkAllBalances(refresh);
      const data = res?.data?.data || res?.data;
      if (data) {
        setSmsProviders((prev) =>
          prev.map((item) => {
            const providerData = data[item.slug];
            if (providerData && providerData.status === 'success') {
              return {
                ...item,
                balanceUnits: providerData.balanceUnits,
                balanceNaira: providerData.balanceNaira || providerData.balanceUnits * 3.5,
              };
            }
            return item;
          })
        );
      }
    } catch (err) {
      // Keep local state on error
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await getNotificationHistory({ limit: 20 });
      const data = res?.data?.data || res?.data;
      if (data && Array.isArray(data.broadcasts) && data.broadcasts.length > 0) {
        setDispatches(
          data.broadcasts.map((b) => ({
            id: b.id,
            channel: b.channel,
            title: b.title,
            summary: b.summary || b.content,
            target: b.target,
            recipientsCount: b.recipientsCount,
            provider: b.provider,
            providerId: b.providerId,
            deliveryRate: b.deliveryRate,
            status: b.status,
            createdAt: new Date(b.createdAt).toLocaleString(),
            raw: b,
          }))
        );
      }
    } catch (err) {
      // Keep local state on error
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await getSmsProvidersConfig();
      const data = res?.data?.data || res?.data;
      if (data) {
        setSmsProviders((prev) =>
          prev.map((item) => {
            const cfg = data[item.slug];
            if (cfg) {
              return {
                ...item,
                status: cfg.enabled ? 'configured' : 'disabled',
                senderId: cfg.senderId || item.senderId,
                username: cfg.username !== undefined ? cfg.username : item.username,
                password: cfg.passwordMasked || item.password,
                apiKey: cfg.apiKeyMasked || item.apiKey,
                whatsappSender: cfg.whatsappSender || item.whatsappSender,
                emailSender: cfg.emailSender || item.emailSender,
                emailSenderName: cfg.emailSenderName || item.emailSenderName,
                dndPolicy: cfg.dndPolicy || item.dndPolicy,
                isDefault: data.defaultSmsProvider === item.slug,
              };
            }
            return item;
          })
        );
      }
    } catch (err) {
      // Keep local state on error
    }
  }, []);

  useEffect(() => {
    fetchBalances();
    fetchHistory();
    fetchConfig();
  }, [fetchBalances, fetchHistory, fetchConfig]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleInsertSmsVariable = (tag) => setSmsBody((prev) => `${prev} ${tag}`);
  const handleInsertWhatsappVariable = (tag) => setWhatsappBody((prev) => `${prev} ${tag}`);

  const handleSetDefaultProvider = (slug) => {
    setSmsProviders((prev) =>
      prev.map((item) => {
        let { status } = item;
        if (item.slug === slug) status = 'active';
        else if (item.status === 'active') status = 'configured';
        return { ...item, isDefault: item.slug === slug, status };
      })
    );
    toast.success(`${slug.toUpperCase()} is now your default SMS provider`);
  };

  const handleProviderFieldChange = (field, value) => {
    setSmsProviders((prev) =>
      prev.map((item) => (item.slug === selectedProviderSlug ? { ...item, [field]: value } : item))
    );
  };

  const handleSendTestSms = async () => {
    if (!testPhoneNumber.trim()) {
      toast.error('Enter a phone number for the test');
      return;
    }
    setTestingSms(true);
    try {
      const res = await testSmsConnection({
        providerSlug: selectedProviderSlug,
        recipientPhone: testPhoneNumber.trim(),
        senderId: activeSmsProvider.senderId,
      });
      const data = res?.data?.data || res?.data;
      if (data?.success) {
        toast.success(`Test SMS sent via ${activeSmsProvider.name} to ${testPhoneNumber}`);
        fetchHistory();
      } else {
        toast.error(`Test failed: ${data?.error || 'Unknown error'}`);
        fetchHistory();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to dispatch test SMS');
    } finally {
      setTestingSms(false);
    }
  };

  const handleSaveSmsConfig = async () => {
    setSavingConfig(true);
    try {
      const ht = smsProviders.find((p) => p.slug === 'hollatags');
      const kd = smsProviders.find((p) => p.slug === 'kudisms');
      const payload = {
        defaultSmsProvider: defaultSmsProvider.slug,
        hollatags: {
          enabled: Boolean(ht?.username && ht?.password),
          username: ht?.username?.trim() || '',
          password: ht?.password || '',
          senderId: ht?.senderId || '',
          whatsappSender: ht?.whatsappSender || '',
          dndPolicy: ht?.dndPolicy || 'corporate',
        },
        kudisms: {
          enabled: Boolean(kd?.apiKey),
          apiKey: kd?.apiKey?.trim() || '',
          senderId: kd?.senderId || '',
          whatsappSender: kd?.whatsappSender || '',
          emailSender: kd?.emailSender || '',
          emailSenderName: kd?.emailSenderName || '',
          dndPolicy: kd?.dndPolicy || 'corporate',
        },
      };
      await updateSmsProvidersConfig(payload);
      toast.success('SMS and Notification settings saved');
      setIsSmsConfigOpen(false);
      fetchBalances(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save settings');
    } finally {
      setSavingConfig(false);
    }
  };

  const getDispatchTitle = (ch) => {
    if (ch === 'whatsapp') {
      return whatsappHeader ? `WhatsApp: ${whatsappHeader}` : `WhatsApp: ${whatsappBody.slice(0, 30)}...`;
    }
    if (ch === 'sms') return `${smsSenderId}: ${smsBody.slice(0, 30)}...`;
    if (ch === 'email') return emailSubject;
    if (ch === 'multi') return multiTitle;
    return pushTitle;
  };

  const getDispatchSummary = (ch) => {
    if (ch === 'whatsapp') return whatsappBody;
    if (ch === 'sms') return smsBody;
    if (ch === 'email') return emailBody;
    if (ch === 'multi') return multiBody;
    return pushBody;
  };

  const getDispatchTarget = (aud) => {
    if (aud === 'specific') return specificTarget || 'One person';
    return AUDIENCES.find((a) => a.value === aud)?.label || 'Custom audience';
  };

  const getDispatchProvider = (ch) => {
    if (ch === 'whatsapp') return 'WhatsApp Cloud API';
    if (ch === 'sms') return `${defaultSmsProvider.name} (${defaultSmsProvider.dndPolicy})`;
    if (ch === 'email') return 'Resend / SMTP';
    if (ch === 'multi') return 'Multi-Channel Hub';
    return 'FCM (Firebase)';
  };

  const handleOpenCompose = (chKey) => {
    setChannel(chKey);
    setIsComposeDrawerOpen(true);
  };

  const handleOpenDetails = async (row) => {
    setSelectedDispatch(row);
    setIsDetailsDrawerOpen(true);
    if (typeof row.id === 'number') {
      try {
        const res = await getBroadcastDetails(row.id);
        const data = res?.data?.data || res?.data;
        if (data) {
          setSelectedDispatch((prev) => ({
            ...prev,
            ...data,
            providerId: data.providerId,
            metadata: data.metadata,
          }));
        }
      } catch (err) {
        // Keep row
      }
    }
  };

  const handleDuplicateDispatch = (row) => {
    if (!row) return;
    setChannel(row.channel);
    if (row.channel === 'push') {
      setPushTitle(row.title);
      setPushBody(row.summary);
    } else if (row.channel === 'email') {
      setEmailSubject(row.title);
      setEmailBody(row.summary);
    } else if (row.channel === 'sms') {
      setSmsBody(row.summary);
    } else if (row.channel === 'whatsapp') {
      setWhatsappBody(row.summary);
    } else {
      setMultiTitle(row.title);
      setMultiBody(row.summary);
    }
    setIsDetailsDrawerOpen(false);
    setIsComposeDrawerOpen(true);
    toast.info('Loaded broadcast into composer');
  };

  const handleClearForm = () => {
    if (channel === 'push') {
      setPushTitle('');
      setPushBody('');
    } else if (channel === 'email') {
      setEmailSubject('');
      setEmailBody('');
    } else if (channel === 'sms') {
      setSmsBody('');
    } else if (channel === 'whatsapp') {
      setWhatsappHeader('');
      setWhatsappBody('');
    } else {
      setMultiTitle('');
      setMultiBody('');
    }
  };

  const handleDispatchBroadcast = async () => {
    if (audience === 'specific' && !specificTarget.trim()) {
      toast.error('Enter a username or phone number');
      return;
    }
    if (channel === 'multi' && !multiPush && !multiEmail && !multiSms && !multiWhatsapp) {
      toast.error('Turn on at least one channel');
      return;
    }
    setSending(true);
    try {
      const payload = {
        channel: channel === 'multi' ? 'omni' : channel,
        target: audience === 'specific' ? 'single' : audience,
        recipient: audience === 'specific' ? specificTarget.trim() : undefined,
        title: getDispatchTitle(channel),
        message: getDispatchSummary(channel),
        senderId: channel === 'sms' ? smsSenderId : undefined,
        provider: channel === 'sms' ? defaultSmsProvider.slug : undefined,
      };

      const res = await sendNotificationBroadcast(payload);
      const data = res?.data?.data || res?.data;

      const newDispatch = {
        id: data?.id || `dsp_${Date.now().toString().slice(-4)}`,
        channel,
        title: data?.title || getDispatchTitle(channel),
        summary: data?.summary || getDispatchSummary(channel),
        target: getDispatchTarget(audience),
        recipientsCount: data?.recipientsCount ?? 1,
        provider: data?.provider || getDispatchProvider(channel),
        providerId: data?.providerId,
        deliveryRate: data?.deliveryRate || '100.0%',
        status: data?.status || 'delivered',
        createdAt: 'Just now',
        raw: data,
      };
      setDispatches((prev) => [newDispatch, ...prev]);
      setIsComposeDrawerOpen(false);
      toast.success(`${CHANNEL_LABEL[channel]} message sent successfully`);
      fetchBalances(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to dispatch notification');
    } finally {
      setSending(false);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const surface = {
    border: `1px solid ${line}`,
    borderRadius: 1,
    bgcolor: 'background.paper',
  };

  const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 1 } };

  // ── Views ──────────────────────────────────────────────────────────────────

  const renderStats = () => {
    const totalDispatchesCount = dispatches.length;
    const deliveredCount = dispatches.filter((d) => d.status === 'delivered').length;
    const activeGateways = smsProviders.filter((p) => p.status === 'configured' || p.status === 'active').length;

    return (
      <Box
        sx={{
          ...surface,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          mb: 2.5,
          overflow: 'hidden',
        }}
      >
        {[
          { label: 'Dispatches', value: totalDispatchesCount.toLocaleString(), note: 'Total Sent' },
          { label: 'Delivered', value: deliveredCount.toLocaleString(), note: 'Completed' },
          { label: 'Gateways', value: `${activeGateways}/2`, note: 'Configured' },
        ].map((s, index) => (
          <Box
            key={s.label}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRight: { xs: index % 2 === 0 ? `1px solid ${line}` : 'none', sm: `1px solid ${line}` },
              borderBottom: { xs: index < 2 ? `1px solid ${line}` : 'none', sm: 'none' },
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {s.note}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1.3 }}>
              {s.value}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {s.label}
            </Typography>
          </Box>
        ))}
        <Box
          component="button"
          type="button"
          onClick={() => setIsSmsConfigOpen(true)}
          sx={{
            p: { xs: 1.5, sm: 2 },
            textAlign: 'left',
            border: 0,
            bgcolor: 'transparent',
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
            '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            SMS · {defaultSmsProvider.name}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1.3 }}>
            ₦{defaultSmsProvider.balanceNaira.toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Manage
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderAudience = () => (
    <Box sx={{ mb: 3 }}>
      <FieldLabel>Send to</FieldLabel>
      <FormControl fullWidth>
        <Select
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          sx={{ borderRadius: 1 }}
          renderValue={(v) => {
            const a = AUDIENCES.find((x) => x.value === v);
            return a ? a.label : v;
          }}
        >
          {AUDIENCES.map((a) => (
            <MenuItem key={a.value} value={a.value}>
              <span>{a.label}</span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {audience === 'specific' && (
        <TextField
          fullWidth
          placeholder="@username or 08012345678"
          value={specificTarget}
          onChange={(e) => setSpecificTarget(e.target.value)}
          sx={{ mt: 1.5, ...fieldSx }}
          inputProps={{ inputMode: 'text', autoCapitalize: 'none' }}
        />
      )}
    </Box>
  );

  const renderPushForm = () => (
    <Stack spacing={2.5}>
      <TextField
        fullWidth
        label="Title"
        value={pushTitle}
        onChange={(e) => setPushTitle(e.target.value)}
        sx={fieldSx}
      />
      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Message"
        value={pushBody}
        onChange={(e) => setPushBody(e.target.value)}
        sx={fieldSx}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          label="Opens screen"
          value={pushActionUrl}
          onChange={(e) => setPushActionUrl(e.target.value)}
          placeholder="/buy-data"
          sx={fieldSx}
        />
        <FormControl fullWidth sx={{ maxWidth: { sm: 180 } }}>
          <InputLabel>Priority</InputLabel>
          <Select
            value={pushPriority}
            label="Priority"
            onChange={(e) => setPushPriority(e.target.value)}
            sx={{ borderRadius: 1 }}
          >
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );

  const renderEmailForm = () => (
    <Stack spacing={2.5}>
      <TextField
        fullWidth
        label="Subject"
        value={emailSubject}
        onChange={(e) => setEmailSubject(e.target.value)}
        sx={fieldSx}
      />
      <TextField
        fullWidth
        multiline
        minRows={6}
        label="Message"
        value={emailBody}
        onChange={(e) => setEmailBody(e.target.value)}
        helperText="You can use {name}, {username} and {balance}"
        sx={fieldSx}
      />
      <Box>
        <Button
          size="small"
          color="inherit"
          onClick={() => setShowEmailExtras((v) => !v)}
          endIcon={<ChevronDown size={16} style={{ transform: showEmailExtras ? 'rotate(180deg)' : 'none' }} />}
          sx={{ textTransform: 'none', color: 'text.secondary', px: 0.5 }}
        >
          Button, sender and preview text
        </Button>
        <Collapse in={showEmailExtras}>
          <Stack spacing={2.5} sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Sender name"
              value={emailSenderName}
              onChange={(e) => setEmailSenderName(e.target.value)}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label="Preview text"
              value={emailPreheader}
              onChange={(e) => setEmailPreheader(e.target.value)}
              helperText="Shown in the inbox before the email is opened"
              sx={fieldSx}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Button text"
                value={emailCtaText}
                onChange={(e) => setEmailCtaText(e.target.value)}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                label="Button link"
                value={emailCtaUrl}
                onChange={(e) => setEmailCtaUrl(e.target.value)}
                inputProps={{ inputMode: 'url', autoCapitalize: 'none' }}
                sx={fieldSx}
              />
            </Stack>
          </Stack>
        </Collapse>
      </Box>
    </Stack>
  );

  const renderSmsForm = () => (
    <Stack spacing={2.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: alpha(theme.palette.grey[500], 0.08) }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Sending with{' '}
          <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {defaultSmsProvider.name}
          </Box>
          {' · '}
          {defaultSmsProvider.balanceUnits.toLocaleString()} units
        </Typography>
        <Button
          size="small"
          onClick={() => setIsSmsConfigOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0 }}
        >
          Change
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          label="Sender ID"
          value={smsSenderId}
          onChange={(e) => setSmsSenderId(e.target.value.toUpperCase().slice(0, 11))}
          helperText="Up to 11 letters or numbers"
          sx={fieldSx}
        />
        <FormControl fullWidth>
          <InputLabel>Route</InputLabel>
          <Select value={smsRoute} label="Route" onChange={(e) => setSmsRoute(e.target.value)} sx={{ borderRadius: 1 }}>
            <MenuItem value="corporate">Corporate (reaches DND numbers)</MenuItem>
            <MenuItem value="standard">Standard</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Box>
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Message"
          value={smsBody}
          onChange={(e) => setSmsBody(e.target.value)}
          sx={fieldSx}
        />
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, px: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: smsCharCount > 160 ? 'warning.main' : 'text.secondary', fontWeight: 500 }}
          >
            {smsCharCount} characters · {smsPageCount} page{smsPageCount > 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {smsPageCount} unit{smsPageCount > 1 ? 's' : ''}/recipient
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
          {['{name}', '{username}', '{balance}', '{app_name}'].map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" onClick={() => handleInsertSmsVariable(tag)} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );

  const renderWhatsappForm = () => (
    <Stack spacing={2.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: alpha(theme.palette.success.main, 0.08) }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Sending via{' '}
          <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
            WhatsApp Cloud API (Verified Business)
          </Box>
        </Typography>
        <Chip label="Meta Cloud API" size="small" color="success" variant="outlined" sx={{ height: 22, fontWeight: 600 }} />
      </Stack>

      <TextField
        fullWidth
        label="Header (Optional)"
        placeholder="e.g. Exclusive Weekend Offer"
        value={whatsappHeader}
        onChange={(e) => setWhatsappHeader(e.target.value)}
        helperText="Bold title displayed at the top of the WhatsApp message"
        sx={fieldSx}
      />

      <Box>
        <TextField
          fullWidth
          multiline
          minRows={5}
          label="Message body"
          value={whatsappBody}
          onChange={(e) => setWhatsappBody(e.target.value)}
          helperText="Format with *bold*, _italic_, or ~strikethrough~. Supports variable placeholders."
          sx={fieldSx}
        />
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
          {['{name}', '{username}', '{balance}', '{app_name}'].map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" onClick={() => handleInsertWhatsappVariable(tag)} />
          ))}
        </Stack>
      </Box>

      <Box>
        <Button
          size="small"
          color="inherit"
          onClick={() => setShowWhatsappExtras((v) => !v)}
          endIcon={<ChevronDown size={16} style={{ transform: showWhatsappExtras ? 'rotate(180deg)' : 'none' }} />}
          sx={{ textTransform: 'none', color: 'text.secondary', px: 0.5 }}
        >
          Call-to-Action (CTA) Button
        </Button>
        <Collapse in={showWhatsappExtras}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Button text"
              value={whatsappButtonText}
              onChange={(e) => setWhatsappButtonText(e.target.value)}
              placeholder="e.g. Top Up Wallet"
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label="Button link (URL)"
              value={whatsappButtonUrl}
              onChange={(e) => setWhatsappButtonUrl(e.target.value)}
              placeholder="https://app.alissoft.com.ng"
              inputProps={{ inputMode: 'url', autoCapitalize: 'none' }}
              sx={fieldSx}
            />
          </Stack>
        </Collapse>
      </Box>
    </Stack>
  );

  const renderMultiForm = () => (
    <Stack spacing={2.5}>
      <Box>
        <FieldLabel>Send through</FieldLabel>
        <Stack direction="row" sx={{ flexWrap: 'wrap', columnGap: 1 }}>
          <FormControlLabel
            control={<Switch checked={multiPush} onChange={(e) => setMultiPush(e.target.checked)} />}
            label="Push"
          />
          <FormControlLabel
            control={<Switch checked={multiEmail} onChange={(e) => setMultiEmail(e.target.checked)} />}
            label="Email"
          />
          <FormControlLabel
            control={<Switch checked={multiSms} onChange={(e) => setMultiSms(e.target.checked)} />}
            label="SMS"
          />
          <FormControlLabel
            control={<Switch checked={multiWhatsapp} onChange={(e) => setMultiWhatsapp(e.target.checked)} />}
            label="WhatsApp"
          />
        </Stack>
      </Box>
      <TextField
        fullWidth
        label="Title"
        value={multiTitle}
        onChange={(e) => setMultiTitle(e.target.value)}
        sx={fieldSx}
      />
      <TextField
        fullWidth
        multiline
        minRows={4}
        label="Message"
        value={multiBody}
        onChange={(e) => setMultiBody(e.target.value)}
        sx={fieldSx}
      />
    </Stack>
  );

  const renderChannelCards = () => (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 1.5,
        }}
      >
        {CHANNEL_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <Box
              key={card.key}
              component="button"
              type="button"
              onClick={() => handleOpenCompose(card.key)}
              sx={{
                ...surface,
                p: 2,
                borderRadius: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                border: `1px solid ${line}`,
                font: 'inherit',
                cursor: 'pointer',
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 20px ${alpha(card.brandColor, 0.14)}`,
                  borderColor: card.brandColor,
                  bgcolor: alpha(card.brandColor, 0.02),
                },
                '&:focus-visible': {
                  outline: `2px solid ${card.brandColor}`,
                },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', mb: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: card.brandBg,
                    color: card.brandColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={18} />
                </Box>
                <ChevronRight size={15} style={{ color: theme.palette.text.secondary, opacity: 0.7 }} />
              </Stack>

              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.25, color: 'text.primary' }}>
                {card.title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.35, fontSize: '0.75rem' }}>
                {card.subtitle}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const renderRecentBroadcastsPreview = () => (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            Recent Broadcasts
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Latest notifications delivered to your audience
          </Typography>
        </Box>
        <Button
          size="small"
          color="inherit"
          endIcon={<ChevronRight size={14} />}
          onClick={() => setMainTab('history')}
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
        >
          View all history
        </Button>
      </Stack>

      <Box sx={{ ...surface, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Channel', 'Message', 'Sent to', 'Delivery', 'When'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, color: 'text.secondary', py: 1.25 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dispatches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 3.5, textAlign: 'center', color: 'text.secondary', fontSize: '0.84rem' }}>
                    No broadcasts sent yet. Click any channel above to compose your first notification.
                  </TableCell>
                </TableRow>
              ) : (
                dispatches.slice(0, 4).map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => handleOpenDetails(row)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ py: 1.25 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ChannelIcon channel={row.channel} size={15} />
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                          {CHANNEL_LABEL[row.channel]}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, py: 1.25 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }} noWrap>
                        {row.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                        {row.summary}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                        {row.target}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {row.recipientsCount.toLocaleString()} people
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.8125rem', py: 1.25 }}>
                      {row.deliveryRate}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontSize: '0.8125rem', py: 1.25 }}>
                      {row.createdAt}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );

  const renderComposeDrawer = () => {
    const cardConfig = CHANNEL_CARDS.find((c) => c.key === channel) || CHANNEL_CARDS[0];
    const CardIcon = cardConfig.icon;

    return (
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={isComposeDrawerOpen}
        onClose={() => setIsComposeDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 540, md: 580 },
            maxHeight: { xs: '92vh', sm: '100%' },
            height: { xs: 'auto', sm: '100%' },
            borderRadius: { xs: '20px 20px 0 0', sm: 0 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${line}` }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: cardConfig.brandBg,
                color: cardConfig.brandColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CardIcon size={20} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
                Compose {CHANNEL_LABEL[channel]}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {cardConfig.subtitle}
              </Typography>
            </Box>
          </Stack>

          <IconButton onClick={() => setIsComposeDrawerOpen(false)} aria-label="Close">
            <X size={20} />
          </IconButton>
        </Stack>

        {/* Content */}
        <Box sx={{ px: 2.5, py: 2.5, flexGrow: 1, overflowY: 'auto' }}>
          {renderAudience()}
          <Divider sx={{ mb: 2.5 }} />

          {channel === 'push' && renderPushForm()}
          {channel === 'email' && renderEmailForm()}
          {channel === 'sms' && renderSmsForm()}
          {channel === 'whatsapp' && renderWhatsappForm()}
          {channel === 'multi' && renderMultiForm()}
        </Box>

        {/* Sticky Actions Footer */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderTop: `1px solid ${line}`,
            bgcolor: 'background.paper',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              color="inherit"
              onClick={handleClearForm}
              sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0 }}
            >
              Clear
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => toast.info('Test sent to your admin account')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1, borderColor: line }}
            >
              Send test
            </Button>
            <LoadingButton
              variant="contained"
              loading={sending}
              onClick={handleDispatchBroadcast}
              startIcon={<Send size={16} />}
              disableElevation
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, px: 2.5 }}
            >
              Send now
            </LoadingButton>
          </Stack>
        </Box>
      </Drawer>
    );
  };

  const renderDetailsDrawer = () => {
    if (!selectedDispatch) return null;
    const ch = selectedDispatch.channel;
    const cardConfig = CHANNEL_CARDS.find((c) => c.key === ch) || CHANNEL_CARDS[0];
    const CardIcon = cardConfig.icon;

    return (
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={isDetailsDrawerOpen}
        onClose={() => setIsDetailsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 500, md: 540 },
            maxHeight: { xs: '92vh', sm: '100%' },
            height: { xs: 'auto', sm: '100%' },
            borderRadius: { xs: '20px 20px 0 0', sm: 0 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${line}` }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: cardConfig.brandBg,
                color: cardConfig.brandColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CardIcon size={18} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                Broadcast Details
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Ref: {selectedDispatch.id}
              </Typography>
            </Box>
          </Stack>

          <IconButton onClick={() => setIsDetailsDrawerOpen(false)} aria-label="Close">
            <X size={20} />
          </IconButton>
        </Stack>

        {/* Scrollable Body */}
        <Box sx={{ px: 2.5, py: 2.5, flexGrow: 1, overflowY: 'auto' }}>
          {/* Top Status Banner */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: alpha(cardConfig.brandColor, 0.05),
              border: `1px solid ${alpha(cardConfig.brandColor, 0.15)}`,
              mb: 2.5,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Chip
                label={CHANNEL_LABEL[ch]}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  bgcolor: alpha(cardConfig.brandColor, 0.12),
                  color: cardConfig.brandColor,
                }}
              />
              <Chip
                label={selectedDispatch.status === 'delivered' ? 'Delivered' : selectedDispatch.status}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600, height: 22 }}
              />
            </Stack>

            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'text.primary', mb: 0.5 }}>
              {selectedDispatch.title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Sent {selectedDispatch.createdAt}
            </Typography>
          </Box>

          {/* Delivery & Route Stats Grid */}
          <Box sx={{ ...surface, p: 2, mb: 2.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Delivery Overview
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              {[
                { label: 'Audience Target', value: selectedDispatch.target },
                { label: 'Recipients Count', value: `${selectedDispatch.recipientsCount.toLocaleString()} users` },
                { label: 'Gateway Provider', value: selectedDispatch.provider },
                { label: 'Provider Message ID', value: selectedDispatch.providerId || 'N/A' },
                { label: 'Success Rate', value: selectedDispatch.deliveryRate, color: 'success.main', bold: true },
                { label: 'Timestamp', value: selectedDispatch.createdAt },
              ].map((item) => (
                <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: item.bold ? 700 : 500,
                      fontSize: '0.84rem',
                      color: item.color || 'text.primary',
                    }}
                  >
                    {item.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {/* Message Content */}
          <Box sx={{ ...surface, p: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Message Payload
            </Typography>
            <Box
              sx={{
                mt: 1.5,
                p: 2,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.grey[500], 0.06),
                fontSize: '0.875rem',
                lineHeight: 1.6,
                color: 'text.primary',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {selectedDispatch.summary}
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderTop: `1px solid ${line}`,
            bgcolor: 'background.paper',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIsDetailsDrawerOpen(false)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1, borderColor: line }}
            >
              Close
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              disableElevation
              startIcon={<Send size={15} />}
              onClick={() => handleDuplicateDispatch(selectedDispatch)}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, px: 2 }}
            >
              Use as template
            </Button>
          </Stack>
        </Box>
      </Drawer>
    );
  };

  const renderHistory = () => {
    if (dispatches.length === 0) {
      return (
        <Box sx={{ ...surface, p: 6, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            No broadcasts yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Dispatches you send via SMS, WhatsApp, Email, or Push will appear here.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {/* Mobile: list */}
      <Stack sx={{ display: { xs: 'flex', md: 'none' }, ...surface }} divider={<Divider />}>
        {dispatches.map((row) => (
          <Box
            key={row.id}
            onClick={() => handleOpenDetails(row)}
            sx={{
              p: 2,
              cursor: 'pointer',
              transition: 'background-color .15s',
              '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: 'text.secondary' }}>
                <ChannelIcon channel={row.channel} size={14} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {CHANNEL_LABEL[row.channel]}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {row.createdAt}
              </Typography>
            </Stack>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.35 }}>{row.title}</Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.25,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {row.summary}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
              {row.target} · {row.recipientsCount.toLocaleString()} people ·{' '}
              <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
                {row.deliveryRate} delivered
              </Box>
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Desktop: table */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, ...surface, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Channel', 'Message', 'Sent to', 'Provider', 'Delivered', 'When'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dispatches.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => handleOpenDetails(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ChannelIcon channel={row.channel} size={15} />
                      <span>{CHANNEL_LABEL[row.channel]}</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 340 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} noWrap>
                      {row.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                      {row.summary}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.target}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {row.recipientsCount.toLocaleString()} people
                    </Typography>
                  </TableCell>
                  <TableCell>{row.provider}</TableCell>
                  <TableCell sx={{ color: 'success.main', fontWeight: 600 }}>{row.deliveryRate}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{row.createdAt}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
    );
  };

  const renderConfigDrawer = () => (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={isSmsConfigOpen}
      onClose={() => setIsSmsConfigOpen(false)}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 460 },
          maxHeight: { xs: '92vh', sm: '100%' },
          height: { xs: 'auto', sm: '100%' },
          borderRadius: { xs: '20px 20px 0 0', sm: 0 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${line}` }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          SMS provider
        </Typography>
        <IconButton onClick={() => setIsSmsConfigOpen(false)} aria-label="Close">
          <X size={20} />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2.5, py: 2.5, flexGrow: 1, overflowY: 'auto' }}>
        {/* Provider list */}
        <Box sx={{ ...surface, overflow: 'hidden', mb: 3 }}>
          {smsProviders.map((p, i) => {
            const selected = p.slug === selectedProviderSlug;
            return (
              <Box
                key={p.slug}
                component="button"
                type="button"
                onClick={() => setSelectedProviderSlug(p.slug)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  textAlign: 'left',
                  border: 0,
                  borderTop: i === 0 ? 0 : `1px solid ${line}`,
                  bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                  font: 'inherit',
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    borderRadius: 1,
                    border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.grey[400]}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }} noWrap>
                      {p.name}
                    </Typography>
                    {p.isDefault && <Chip label="Default" size="small" color="primary" sx={{ height: 20 }} />}
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                    {p.tagline}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {p.balanceUnits > 0 ? `${p.balanceUnits.toLocaleString()} units` : 'Not set up'}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Selected provider settings */}
        <Typography sx={{ fontWeight: 700, mb: 2 }}>{activeSmsProvider.name} settings</Typography>

        <Stack spacing={2.5}>
          <FormControlLabel
            sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
            labelPlacement="start"
            control={
              <Switch
                checked={activeSmsProvider.isDefault}
                onChange={(e) => {
                  if (e.target.checked) handleSetDefaultProvider(activeSmsProvider.slug);
                }}
              />
            }
            label="Use as default provider"
          />

          {activeSmsProvider.slug === 'hollatags' ? (
            <>
              <TextField
                fullWidth
                label="Username"
                value={activeSmsProvider.username || ''}
                onChange={(e) => handleProviderFieldChange('username', e.target.value)}
                placeholder="e.g. your_username"
                helperText="HollaTags account username or API user ID"
                sx={fieldSx}
              />
              <TextField
                fullWidth
                label="Password / Secret"
                type={showPassword ? 'text' : 'password'}
                value={activeSmsProvider.password || ''}
                onChange={(e) => handleProviderFieldChange('password', e.target.value)}
                helperText="HollaTags account password / API secret"
                sx={fieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : (
            <TextField
              fullWidth
              label="API Token"
              type={showApiKey ? 'text' : 'password'}
              value={activeSmsProvider.apiKey || ''}
              onChange={(e) => handleProviderFieldChange('apiKey', e.target.value)}
              helperText="Private API token from your KudiSMS dashboard"
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowApiKey(!showApiKey)}
                      aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                    >
                      {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <TextField
            fullWidth
            label="Sender ID"
            value={activeSmsProvider.senderId || ''}
            onChange={(e) => handleProviderFieldChange('senderId', e.target.value.toUpperCase().slice(0, 11))}
            helperText="Alphanumeric sender ID registered with telcos (max 11 chars)"
            sx={fieldSx}
          />

          <Box>
            <Button
              size="small"
              color="inherit"
              onClick={() => setShowAdvanced((v) => !v)}
              endIcon={<ChevronDown size={16} style={{ transform: showAdvanced ? 'rotate(180deg)' : 'none' }} />}
              sx={{ textTransform: 'none', color: 'text.secondary', px: 0.5 }}
            >
              Advanced
            </Button>
            <Collapse in={showAdvanced}>
              <Stack spacing={2.5} sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label="API endpoint"
                  value={activeSmsProvider.baseUrl || ''}
                  onChange={(e) => handleProviderFieldChange('baseUrl', e.target.value)}
                  inputProps={{ inputMode: 'url', autoCapitalize: 'none' }}
                  sx={fieldSx}
                />
                <FormControl fullWidth>
                  <InputLabel>Route type</InputLabel>
                  <Select
                    value={activeSmsProvider.dndPolicy || 'corporate'}
                    label="Route type"
                    onChange={(e) => handleProviderFieldChange('dndPolicy', e.target.value)}
                    sx={{ borderRadius: 1 }}
                  >
                    <MenuItem value="corporate">Corporate (reaches DND numbers)</MenuItem>
                    <MenuItem value="standard">Standard</MenuItem>
                  </Select>
                </FormControl>

                {activeSmsProvider.slug === 'kudisms' && (
                  <>
                    <TextField
                      fullWidth
                      label="Email Sender Address"
                      value={activeSmsProvider.emailSender || ''}
                      onChange={(e) => handleProviderFieldChange('emailSender', e.target.value)}
                      placeholder="e.g. support@example.com"
                      helperText="Verified sender email on KudiSMS"
                      sx={fieldSx}
                    />
                    <TextField
                      fullWidth
                      label="Email Sender Name"
                      value={activeSmsProvider.emailSenderName || ''}
                      onChange={(e) => handleProviderFieldChange('emailSenderName', e.target.value)}
                      placeholder="e.g. Alisbuild Support"
                      helperText="Sender display name"
                      sx={fieldSx}
                    />
                  </>
                )}

                <TextField
                  fullWidth
                  label="WhatsApp Sender Number"
                  value={activeSmsProvider.whatsappSender || ''}
                  onChange={(e) => handleProviderFieldChange('whatsappSender', e.target.value)}
                  placeholder="e.g. 2348012345678"
                  helperText="Approved WhatsApp Business phone number (optional)"
                  sx={fieldSx}
                />
              </Stack>
            </Collapse>
          </Box>

          <Divider />

          <Box>
            <FieldLabel>Send a test SMS</FieldLabel>
            <Stack direction="row" spacing={1.5}>
              <TextField
                fullWidth
                placeholder="08012345678"
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
                inputProps={{ inputMode: 'tel' }}
                sx={fieldSx}
              />
              <LoadingButton
                variant="outlined"
                color="inherit"
                loading={testingSms}
                onClick={handleSendTestSms}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1,
                  borderColor: line,
                  whiteSpace: 'nowrap',
                }}
              >
                Send
              </LoadingButton>
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: `1px solid ${line}`,
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        }}
      >
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          disableElevation
          loading={savingConfig}
          onClick={handleSaveSmsConfig}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1 }}
        >
          Save changes
        </LoadingButton>
      </Box>
    </Drawer>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  const inHistory = mainTab === 'history';

  return (
    <Page title="Notifications & Messaging">
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 3 }, pb: 4 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {inHistory && (
              <IconButton onClick={() => setMainTab('overview')} aria-label="Back to channels" edge="start">
                <ArrowLeft size={20} />
              </IconButton>
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {inHistory ? 'Broadcast History' : 'Notifications & Messaging'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {inHistory ? 'Complete log of sent broadcasts and campaigns' : 'Choose a channel to compose and send messages'}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {inHistory ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<Send size={15} />}
                onClick={() => handleOpenCompose('sms')}
                disableElevation
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1 }}
              >
                New broadcast
              </Button>
            ) : (
              <IconButton
                onClick={() => setMainTab('history')}
                aria-label="History"
                sx={{ border: `1px solid ${line}` }}
              >
                <Clock size={18} />
              </IconButton>
            )}
            <IconButton
              onClick={() => setIsSmsConfigOpen(true)}
              aria-label="SMS provider settings"
              sx={{ border: `1px solid ${line}` }}
            >
              <Settings size={18} />
            </IconButton>
          </Stack>
        </Stack>

        {!inHistory && renderStats()}

        {inHistory ? (
          renderHistory()
        ) : (
          <>
            {renderChannelCards()}
            {renderRecentBroadcastsPreview()}
          </>
        )}

        {renderDetailsDrawer()}
        {renderComposeDrawer()}
        {renderConfigDrawer()}
      </Container>
    </Page>
  );
}
